import React from "react";
import { Box, Table, TableBody, TableCell, TableRow, Typography } from "@mui/material";
import {
  BRAND_WORLD_ADDRESS_LINE,
  BRAND_WORLD_INVOICE_ISSUER,
  BRAND_WORLD_LOGO_URL,
  isBrandWorldParty,
} from "../../../utils/invoiceIssuer";

const titleStyle = { fontFamily: "Poppins", fontWeight: 500, fontSize: "12px", color: "rgba(107,122,153,1)" };
const valueStyle = { fontFamily: "Poppins", fontSize: "12px", color: "rgba(80,80,80,1)" };
const headerTextStyle = { fontFamily: "Poppins", fontWeight: 600, fontSize: "12px", color: "white" };

function getInitials(name) {
  const n = String(name || "").trim();
  if (!n) return "—";
  const parts = n.split(/\s+/).filter(Boolean);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0] || ""}${parts[1][0] || ""}`.toUpperCase();
}

function resolveCompanyLogo(entity, side) {
  if (!entity || typeof entity !== "object") return "";
  const candidates =
    side === "seller"
      ? [
          entity.SellerCompanyLogo,
          entity.sellerCompanyLogo,
          entity.CompanyLogo,
          entity.companyLogo,
          entity.Logo,
          entity.logo,
        ]
      : [
          entity.BuyerCompanyLogo,
          entity.buyerCompanyLogo,
          entity.CompanyLogo,
          entity.companyLogo,
          entity.Logo,
          entity.logo,
        ];
  const logo = candidates.find((v) => typeof v === "string" && v.trim() !== "");
  return logo || "";
}

export default function PISellerBuyerDetails({
  seller,
  buyer,
  sellerGstin,
  buyerGstin,
  headerBg = "#000000",
}) {
  const sellerIsBrandWorld = isBrandWorldParty(seller);
  const buyerIsBrandWorld = isBrandWorldParty(buyer);
  const sellerName = sellerIsBrandWorld
    ? "BRAND WORLD"
    : seller?.SellerCompanyName || seller?.companyName || "—";
  const buyerName = buyerIsBrandWorld
    ? "BRAND WORLD"
    : buyer?.BuyerCompanyName || buyer?.companyName || "—";
  const sellerLogo = sellerIsBrandWorld
    ? BRAND_WORLD_LOGO_URL
    : resolveCompanyLogo(seller, "seller");
  const buyerLogo = buyerIsBrandWorld
    ? BRAND_WORLD_LOGO_URL
    : resolveCompanyLogo(buyer, "buyer");
  const sellerAddressLine = sellerIsBrandWorld
    ? seller?.Address?.AddressLine || BRAND_WORLD_ADDRESS_LINE
    : seller?.Address?.AddressLine;
  const buyerAddressLine = buyerIsBrandWorld
    ? buyer?.Address?.AddressLine || BRAND_WORLD_ADDRESS_LINE
    : buyer?.Address?.AddressLine;
  const sellerGstinValue = sellerGstin || seller?.GSTIN ||
    (sellerIsBrandWorld ? BRAND_WORLD_INVOICE_ISSUER.gstin : "") || "—";
  const buyerGstinValue = buyerGstin || buyer?.GSTIN ||
    (buyerIsBrandWorld ? BRAND_WORLD_INVOICE_ISSUER.gstin : "") || "—";

  return (
    <Box sx={{ width: "100%", mx: "auto", border: "1px solid #cdcdcd" }}>
      <Box
        sx={{
          width: "100%",
          minHeight: "28px",
          background: headerBg,
          borderRadius: "0",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Box sx={{ width: "50%", display: "flex", justifyContent: "center", alignItems: "center", height: "100%", borderRight: "1px solid rgba(255,255,255,0.3)" }}>
          <Typography sx={headerTextStyle}>Seller Details</Typography>
        </Box>
        <Box sx={{ width: "50%", display: "flex", justifyContent: "center", alignItems: "center", height: "100%" }}>
          <Typography sx={headerTextStyle}>Buyer Details</Typography>
        </Box>
      </Box>
      <Box sx={{ display: "flex", flexDirection: "row", justifyContent: "space-between", alignItems: "flex-start", width: "100%", py: 0 }}>
        <Box sx={{ width: "100%", mx: "auto", borderRight: "1px solid #CDCDCD", p: 2 }}>
          <Table size="small" sx={{ p: 0 }}>
            <TableBody sx={{ p: 0 }}>
              <TableRow><TableCell sx={{ p: 0.5, borderBottom: "none", width: "90px" }}><Typography sx={titleStyle}>Seller Name</Typography></TableCell><TableCell sx={{ p: 0.5, borderBottom: "none" }}><Typography sx={valueStyle}>{sellerName}</Typography></TableCell></TableRow>
              <TableRow><TableCell sx={{ p: 0.5, borderBottom: "none" }}><Typography sx={titleStyle}>Address</Typography></TableCell><TableCell sx={{ p: 0.5, borderBottom: "none" }}><Typography sx={valueStyle}>{sellerAddressLine}</Typography></TableCell></TableRow>
              <TableRow><TableCell sx={{ p: 0.5, borderBottom: "none" }}><Typography sx={titleStyle}>GSTIN</Typography></TableCell><TableCell sx={{ p: 0.5, borderBottom: "none" }}><Typography sx={valueStyle}>{sellerGstinValue}</Typography></TableCell></TableRow>
              <TableRow><TableCell sx={{ p: 0.5, borderBottom: "none" }}><Typography sx={titleStyle}>State</Typography></TableCell><TableCell sx={{ p: 0.5, borderBottom: "none" }}><Typography sx={valueStyle}>{seller?.Address?.State || "—"}</Typography></TableCell></TableRow>
            </TableBody>
          </Table>
        </Box>
        <Box sx={{ width: "100%", mx: "auto", p: 2 }}>
          <Table size="small">
            <TableBody>
              <TableRow><TableCell sx={{ p: 0.5, borderBottom: "none", width: "90px" }}><Typography sx={titleStyle}>Buyer Name</Typography></TableCell><TableCell sx={{ p: 0.5, borderBottom: "none" }}><Typography sx={valueStyle}>{buyerName}</Typography></TableCell></TableRow>
              <TableRow><TableCell sx={{ p: 0.5, borderBottom: "none" }}><Typography sx={titleStyle}>Address</Typography></TableCell><TableCell sx={{ p: 0.5, borderBottom: "none" }}><Typography sx={valueStyle}>{buyerAddressLine}</Typography></TableCell></TableRow>
              <TableRow><TableCell sx={{ p: 0.5, borderBottom: "none" }}><Typography sx={titleStyle}>GSTIN</Typography></TableCell><TableCell sx={{ p: 0.5, borderBottom: "none" }}><Typography sx={valueStyle}>{buyerGstinValue}</Typography></TableCell></TableRow>
              <TableRow><TableCell sx={{ p: 0.5, borderBottom: "none" }}><Typography sx={titleStyle}>State</Typography></TableCell><TableCell sx={{ p: 0.5, borderBottom: "none" }}><Typography sx={valueStyle}>{buyer?.Address?.State || "—"}</Typography></TableCell></TableRow>
            </TableBody>
          </Table>
        </Box>
      </Box>
    </Box>
  );
}
